/**
 * Travel-time connector pill (plan D4 / spec "Travel-time surface").
 *
 * Renders BETWEEN consecutive stop cards from the leg's `travelToHere`. The
 * first surviving stop of a day has `travelToHere=null` and renders NO pill
 * (caller enforces this by only mounting the pill when `travel` is non-null).
 * Editorial restraint: one line, muted ink, no map thumbnail. The internal
 * `cached` flag is intentionally never surfaced.
 */

import type { SerializedTravelToHere } from './itineraryTypes';
import { Caption } from '../ui/Typography';
import { useDistanceUnit } from '../../hooks/useDistanceUnit';

interface TravelTimePillProps {
  travel: SerializedTravelToHere;
}

const formatDuration = (seconds: number): string => {
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

const formatDistance = (meters: number, unit: 'mi' | 'km'): string => {
  if (unit === 'km') {
    const km = meters / 1000;
    return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
  }
  const miles = meters / 1609.344;
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
};

export const TravelTimePill = ({ travel }: TravelTimePillProps) => {
  const unit = useDistanceUnit();
  const label = `Drive ${formatDuration(travel.durationSeconds)}, ${formatDistance(
    travel.distanceMeters,
    unit
  )} to the next stop`;
  return (
    <div className="flex justify-center py-1" aria-hidden="false">
      <span
        className="inline-flex items-center gap-2 rounded-full border border-[#DBD4C9] bg-[#F2F0EA] px-3 py-1"
        aria-label={label}
      >
        <span aria-hidden="true">🚗</span>
        <Caption className="text-gray-600">
          {formatDuration(travel.durationSeconds)} ·{' '}
          {formatDistance(travel.distanceMeters, unit)}
        </Caption>
      </span>
    </div>
  );
};
